export type SkinTeam = 'T' | 'CT' | 'both';

export interface SkinDefinition {
  id: number;
  name: string;
  glbPath: string;
  thumbnailPath: string;
  team: SkinTeam;
  fallbackColor: number;
}

/** Default fallback skins (always available, bundled with client) */
export const DEFAULT_T_SKIN_ID = 0;
export const DEFAULT_CT_SKIN_ID = 1;

// L24: Helper to build a ReadonlyMap with duplicate ID detection
function buildSkinManifest(entries: [number, SkinDefinition][]): ReadonlyMap<number, SkinDefinition> {
  const seen = new Set<number>();
  for (const [id] of entries) {
    if (seen.has(id)) {
      console.error(`SkinManifest: duplicate skin ID ${id} detected — last entry wins`);
    }
    seen.add(id);
  }
  return new Map(entries);
}

export const SKIN_MANIFEST: ReadonlyMap<number, SkinDefinition> = buildSkinManifest([
  [0, {
    id: 0,
    name: 'Default T',
    glbPath: 'models/skins/default_t.glb',
    thumbnailPath: 'models/skins/thumbs/default_t.png',
    team: 'T',
    fallbackColor: 0x5c5a3c,
  }],
  [1, {
    id: 1,
    name: 'Default CT',
    glbPath: 'models/skins/default_ct.glb',
    thumbnailPath: 'models/skins/thumbs/default_ct.png',
    team: 'CT',
    fallbackColor: 0x2a3040,
  }],
  [2, {
    id: 2,
    name: 'Diamond Hands',
    glbPath: 'models/skins/diamond_hands.glb',
    thumbnailPath: 'models/skins/thumbs/diamond_hands.png',
    team: 'both',
    fallbackColor: 0x00bfff,
  }],
  [3, {
    id: 3,
    name: 'Paper Hands',
    glbPath: 'models/skins/paper_hands.glb',
    thumbnailPath: 'models/skins/thumbs/paper_hands.png',
    team: 'both',
    fallbackColor: 0xd2b48c,
  }],
  [4, {
    id: 4,
    name: 'Laser Eyes',
    glbPath: 'models/skins/laser_eyes.glb',
    thumbnailPath: 'models/skins/thumbs/laser_eyes.png',
    team: 'CT',
    fallbackColor: 0xff0000,
  }],
  [5, {
    id: 5,
    name: 'Moonboy',
    glbPath: 'models/skins/moonboy.glb',
    thumbnailPath: 'models/skins/thumbs/moonboy.png',
    team: 'CT',
    fallbackColor: 0xffffff,
  }],
  [6, {
    id: 6,
    name: 'The Bull',
    glbPath: 'models/skins/the_bull.glb',
    thumbnailPath: 'models/skins/thumbs/the_bull.png',
    team: 'T',
    fallbackColor: 0x228b22,
  }],
  [7, {
    id: 7,
    name: 'The Bear',
    glbPath: 'models/skins/the_bear.glb',
    thumbnailPath: 'models/skins/thumbs/the_bear.png',
    team: 'T',
    fallbackColor: 0x6a3415,
  }],
  [8, {
    id: 8,
    name: 'The Whale',
    glbPath: 'models/skins/the_whale.glb',
    thumbnailPath: 'models/skins/thumbs/the_whale.png',
    team: 'CT',
    fallbackColor: 0x000080,
  }],
  [9, {
    id: 9,
    name: 'Rug Puller',
    glbPath: 'models/skins/rug_puller.glb',
    thumbnailPath: 'models/skins/thumbs/rug_puller.png',
    team: 'T',
    fallbackColor: 0x800080,
  }],
  [10, {
    id: 10,
    name: 'The Doomer',
    glbPath: 'models/skins/the_doomer.glb',
    thumbnailPath: 'models/skins/thumbs/the_doomer.png',
    team: 'T',
    fallbackColor: 0x1a1a1a,
  }],
  [11, {
    id: 11,
    name: 'The Bloomer',
    glbPath: 'models/skins/the_bloomer.glb',
    thumbnailPath: 'models/skins/thumbs/the_bloomer.png',
    team: 'CT',
    fallbackColor: 0xffb6c1,
  }],
  [12, {
    id: 12,
    name: 'The Chad',
    glbPath: 'models/skins/the_chad.glb',
    thumbnailPath: 'models/skins/thumbs/the_chad.png',
    team: 'both',
    fallbackColor: 0xffd700,
  }],
  [13, {
    id: 13,
    name: 'The NPC',
    glbPath: 'models/skins/the_npc.glb',
    thumbnailPath: 'models/skins/thumbs/the_npc.png',
    team: 'both',
    fallbackColor: 0x4a4a48,
  }],
  [14, {
    id: 14,
    name: 'Crypto Anon',
    glbPath: 'models/skins/crypto_anon.glb',
    thumbnailPath: 'models/skins/thumbs/crypto_anon.png',
    team: 'T',
    fallbackColor: 0x2c6b2c,
  }],
  [15, {
    id: 15,
    name: 'Pepe',
    glbPath: 'models/skins/pepe.glb',
    thumbnailPath: 'models/skins/thumbs/pepe.png',
    team: 'T',
    fallbackColor: 0x3d7b30,
  }],
  [16, {
    id: 16,
    name: 'The Shiba',
    glbPath: 'models/skins/the_shiba.glb',
    thumbnailPath: 'models/skins/thumbs/the_shiba.png',
    team: 'CT',
    fallbackColor: 0xd4943a,
  }],
  [17, {
    id: 17,
    name: 'GigaChad',
    glbPath: 'models/skins/gigachad.glb',
    thumbnailPath: 'models/skins/thumbs/gigachad.png',
    team: 'both',
    fallbackColor: 0x555555,
  }],
  [18, {
    id: 18,
    name: 'Dogecoin',
    glbPath: 'models/skins/dogecoin.glb',
    thumbnailPath: 'models/skins/thumbs/dogecoin.png',
    team: 'T',
    fallbackColor: 0xd4a017,
  }],
  [19, {
    id: 19,
    name: 'Satoshi',
    glbPath: 'models/skins/satoshi.glb',
    thumbnailPath: 'models/skins/thumbs/satoshi.png',
    team: 'T',
    fallbackColor: 0x1a1a26,
  }],
  [20, {
    id: 20,
    name: 'Memelord',
    glbPath: 'models/skins/memelord.glb',
    thumbnailPath: 'models/skins/thumbs/memelord.png',
    team: 'both',
    fallbackColor: 0xc0c0c0,
  }],
  [21, {
    id: 21,
    name: 'BONK',
    glbPath: 'models/skins/bonk.glb',
    thumbnailPath: 'models/skins/thumbs/bonk.png',
    team: 'T',
    fallbackColor: 0xc24810,
  }],
  [22, {
    id: 22,
    name: 'Dogwifhat',
    glbPath: 'models/skins/dogwifhat.glb',
    thumbnailPath: 'models/skins/thumbs/dogwifhat.png',
    team: 'CT',
    fallbackColor: 0xe8a0b0,
  }],
  [23, {
    id: 23,
    name: 'Floki',
    glbPath: 'models/skins/floki.glb',
    thumbnailPath: 'models/skins/thumbs/floki.png',
    team: 'T',
    fallbackColor: 0xd4700a,
  }],
  [24, {
    id: 24,
    name: 'MOG',
    glbPath: 'models/skins/mog.glb',
    thumbnailPath: 'models/skins/thumbs/mog.png',
    team: 'both',
    fallbackColor: 0x888888,
  }],
  [25, {
    id: 25,
    name: 'BRETT',
    glbPath: 'models/skins/brett.glb',
    thumbnailPath: 'models/skins/thumbs/brett.png',
    team: 'CT',
    fallbackColor: 0x0052ff,
  }],
  [26, {
    id: 26,
    name: 'POPCAT',
    glbPath: 'models/skins/popcat.glb',
    thumbnailPath: 'models/skins/thumbs/popcat.png',
    team: 'CT',
    fallbackColor: 0xe8943a,
  }],
  [27, {
    id: 27,
    name: 'SPX6900',
    glbPath: 'models/skins/spx6900.glb',
    thumbnailPath: 'models/skins/thumbs/spx6900.png',
    team: 'T',
    fallbackColor: 0x4fff6b,
  }],
]);
